import React from "react";
import { AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { daysUntilDue } from "@/lib/atlasRequisitions";

const formatDay = (day) => {
  if (!day) return "no due date";
  const [y, m, d] = day.split("-");
  return `${d}/${m}/${y}`;
};

function dueLabel(dueOn) {
  const days = daysUntilDue(dueOn);
  if (days === null) return "No due date given";
  if (days < 0) return `Overdue by ${-days} day${days === -1 ? "" : "s"} (was due ${formatDay(dueOn)})`;
  if (days === 0) return `Due today (${formatDay(dueOn)})`;
  return `Due in ${days} day${days === 1 ? "" : "s"} (${formatDay(dueOn)})`;
}

// Red banner at the top of the Aid Planner for every requisition fetched from ATLAS that
// has not been answered. Requisitions hold up a grant, so this stays until each is marked
// answered (or a later fetch finds ATLAS has received the response).
export default function RequisitionsBanner({ requisitions = [], onOpen, onAnswered }) {
  if (!requisitions.length) return null;
  return (
    <section
      role="alert"
      aria-label="Requisitions to answer"
      className="rounded-2xl border-2 border-red-300 bg-red-50 p-5 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-6 w-6 flex-shrink-0 text-red-600" />
        <div className="flex-1">
          <h3 className="text-lg font-bold text-red-900">
            {requisitions.length === 1 ? "A requisition needs answering" : `${requisitions.length} requisitions need answering`}
          </h3>
          <p className="mt-0.5 text-sm text-red-800">
            VLA has asked for more before deciding. Answer each one in ATLAS (Inbox, then the requisition) before its due date.
          </p>
          <ul className="mt-3 space-y-2">
            {requisitions.map((req) => {
              const days = daysUntilDue(req.dueOn);
              const urgent = days !== null && days <= 3;
              return (
                <li
                  key={`${req.matterId}:${req.id}`}
                  className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3 ${urgent ? "border-red-400" : "border-red-200"}`}
                >
                  <div>
                    <p className="font-bold text-slate-900">
                      {req.clientName || "Unnamed client"}
                      <span className="ml-2 text-xs font-semibold text-slate-500">
                        {req.fileId}{req.extension && req.extension !== "0" ? ` · extension ${req.extension}` : ""}
                      </span>
                    </p>
                    <p className={`text-sm font-semibold ${urgent ? "text-red-700" : "text-red-800"}`}>{dueLabel(req.dueOn)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => onOpen?.(req)}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-xs font-bold text-red-800 hover:bg-red-100"
                    >
                      <ExternalLink className="h-4 w-4" />Open matter
                    </button>
                    <button
                      type="button"
                      onClick={() => onAnswered?.(req)}
                      className="inline-flex items-center gap-1 rounded-lg bg-red-700 px-3 py-2 text-xs font-bold text-white hover:bg-red-800"
                    >
                      <CheckCircle2 className="h-4 w-4" />Mark answered
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
